import { Hero } from '../features/hero/Hero.jsx';
import { Section, Wrap, SectionIntro, Grid } from '../components/ui/Section.jsx';
import { Sequence, Reveal, LineRise, SeqFollow } from '../components/ui/motion.jsx';
import { Eyebrow } from '../components/ui/Eyebrow.jsx';
import { Pillar } from '../components/ui/Pillar.jsx';
import { Button } from '../components/ui/Button.jsx';
import { ProjectCard } from '../components/ui/ProjectCard.jsx';
import { CtaBanner } from '../components/ui/CtaBanner.jsx';
import { philosophyPillars, processSummary } from '../data/content.js';
import { featuredProjects } from '../data/projects.js';

export const meta = {
  title: 'Abode Custom Homes — Custom Home Builder in Bloomington, IL',
  description:
    'Abode Custom Homes designs and builds custom dream homes in Bloomington, Illinois. Crafting custom dream homes with quality craftsmanship tailored just for you.',
};

export default function HomePage() {
  return (
    <>
      <Hero />

      <Section>
        <Sequence as={Wrap}>
          <SectionIntro>
            <div>
              <Eyebrow>Our Philosophy</Eyebrow>
              <LineRise
                className="h2 mt-2"
                lines={['A home built around', 'how you actually live.']}
              />
            </div>
            <SeqFollow as="p" className="lede">
              We&rsquo;re a new studio with an old-fashioned standard: every Abode home is
              planned, drawn, and built around one family — not a floor plan pulled off a shelf.
            </SeqFollow>
          </SectionIntro>

          <Grid cols={3} rowCascade>
            {philosophyPillars.map((pillar, i) => (
              <Pillar key={pillar.num} num={pillar.num} title={pillar.title} index={i}>
                {pillar.body}
              </Pillar>
            ))}
          </Grid>
        </Sequence>
      </Section>

      <Section tone="alt">
        <Sequence as={Wrap}>
          <SectionIntro>
            <div>
              <Eyebrow>How We Build</Eyebrow>
              <LineRise
                className="h2 mt-2"
                lines={['A clear path from', 'first call to move-in.']}
              />
            </div>
            <SeqFollow>
              <Button to="/process" variant="ghost">See the full process</Button>
            </SeqFollow>
          </SectionIntro>

          <Grid cols={4} rowCascade>
            {processSummary.map((step, i) => (
              <Pillar key={step.num} num={step.num} title={step.title} index={i}>
                {step.body}
              </Pillar>
            ))}
          </Grid>
        </Sequence>
      </Section>

      <Section>
        <Wrap>
          <Reveal as={SectionIntro}>
            <div>
              <Eyebrow animated={false}>Featured Projects</Eyebrow>
              <h2 className="h2 mt-2">A first look at<br />what we&rsquo;re building.</h2>
            </div>
            <Button to="/projects" variant="ghost">View all projects</Button>
          </Reveal>

          {/* Sized to what actually exists, so removing a project can't
              leave an empty column behind. */}
          <Grid cols={Math.min(featuredProjects.length, 3)}>
            {featuredProjects.map((project) => (
              <Reveal key={project.slug}>
                <ProjectCard project={project} />
              </Reveal>
            ))}
          </Grid>
        </Wrap>
      </Section>

      {/* The dark closing band the interior pages already use. With the
          testimonial block gone this was the second light section in a row,
          so the closing ask read as a continuation of the projects above it
          rather than as its own moment — and the home page was the only
          page whose final ask did not carry the weight the others give it.
          Full padding, not tight, to match those pages. */}
      <Section tone="dark">
        <Wrap>
          <CtaBanner lines={["Let's build your", 'Abode.']} onDark />
        </Wrap>
      </Section>
    </>
  );
}
